import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Printer, RotateCcw, Search } from 'lucide-react';
import * as api from '../../api';
import { Btn, DatePicker, Input, RNLLoader, Select } from '../UI';
import Pagination from '../requisition/Pagination';
import { StatTile } from '../materialTakeoff/MTOShared';
import { StatusPill } from './MMRegister';
import { MAPPING_STATUS, NAVY, asOptions, coverageLabel, exportXlsx, fmtDate, fmtQty, typeLabel } from './mmUtils';

const th = { padding: '10px 12px', fontSize: 10, fontWeight: 800, color: '#fff', background: '#0b318bff', border: '1px solid black',
             textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 };
const td = { padding: '8px 12px', fontSize: 12.5, borderBottom: '1px solid var(--border-light)', verticalAlign: 'top' };

const EMPTY = { division: '', category: '', subsection: '', status: '', boq_code: '', material_code: '', prepared_by: '', date_from: '', date_to: '', search: '' };

const COLUMNS = [
  { key: 'material_code', label: 'Material Code' }, { key: 'material_name', label: 'Material Description' },
  { key: 'material_category', label: 'Category' }, { key: 'material_uom', label: 'UOM' }, { key: 'building', label: 'Building' },
  { key: 'discipline', label: 'Discipline' }, { key: 'work_package', label: 'Work Package' }, { key: 'boq_code', label: 'BOQ Code' },
  { key: 'description', label: 'BOQ Description' }, { key: 'quantity', label: 'BOQ Qty', value: r => Number(r.quantity) },
  { key: 'unit_of_measure', label: 'BOQ UOM' }, { key: 'mapping_type', label: 'Mapping Type', value: r => typeLabel(r.mapping_type) },
  { key: 'boq_coverage', label: 'BOQ Coverage', value: r => coverageLabel(r.boq_coverage) },
  { key: 'quantity_per_unit', label: 'Qty / BOQ Unit', value: r => (r.quantity_per_unit == null ? '' : Number(r.quantity_per_unit)) },
  { key: 'mapping_ref', label: 'Mapping Ref' }, { key: 'status', label: 'Status', value: r => MAPPING_STATUS[r.status]?.label || r.status },
  { key: 'prepared_by', label: 'Mapped By' }, { key: 'date_prepared', label: 'Mapped Date' },
];

const esc = (v) => String(v ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function printRows(rows, title) {
  const w = window.open('', '_blank');
  if (!w) return;
  const cols = COLUMNS.filter(c => !['material_category', 'quantity_per_unit', 'boq_coverage'].includes(c.key));
  w.document.write(`<!doctype html><html><head><title>${esc(title)}</title><style>
    body{font-family:Arial,sans-serif;font-size:10px;margin:16px} h1{font-size:15px;margin:0 0 4px}
    table{border-collapse:collapse;width:100%} th,td{border:1px solid #333;padding:3px 5px;text-align:left;vertical-align:top}
    th{background:#0b318b;color:#fff;font-size:9px;text-transform:uppercase} @page{size:landscape;margin:10mm}
  </style></head><body><h1>${esc(title)}</h1><div style="margin-bottom:8px">${rows.length} mapping relationships · printed ${new Date().toLocaleString()}</div>
  <table><thead><tr>${cols.map(c => `<th>${esc(c.label)}</th>`).join('')}</tr></thead><tbody>
  ${rows.map(r => `<tr>${cols.map(c => `<td>${esc(c.value ? c.value(r) : r[c.key])}</td>`).join('')}</tr>`).join('')}
  </tbody></table><script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
}

// Materials already linked to BOQ lines: review, trace, verify and export the relationships.
export default function MMMapped({ projectId, project, lists, openMapper, toast }) {
  const [f, setF] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [view, setView] = useState('flat');
  const [page, setPage] = useState({ skip: 0, limit: 50 });
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState('');

  const params = useCallback((extra) => ({
    project_id: projectId, project_division_id: applied.division, dmr_category_id: applied.category, dmr_subsection_id: applied.subsection,
    status: applied.status, boq_code: applied.boq_code, material_code: applied.material_code, prepared_by: applied.prepared_by,
    date_from: applied.date_from, date_to: applied.date_to, search: applied.search, ...extra,
  }), [projectId, applied]);

  useEffect(() => { api.getMappedMaterialStats(projectId).then(setStats).catch(() => setStats(null)); }, [projectId]);

  useEffect(() => {
    setRows(null);
    const grouped = view === 'grouped';
    api.listMappedMaterials(params(grouped ? { skip: 0, limit: 5000 } : { skip: page.skip, limit: page.limit }))
      .then(r => { setRows(r || []); setTotal(r?.totalCount ?? (r || []).length); })
      .catch(e => { setRows([]); toast?.show?.(e.message || 'Could not load mapped materials', 'error'); });
  }, [params, page, view, toast]);

  const groups = useMemo(() => {
    if (view !== 'grouped' || !rows) return [];
    const by = new Map();
    rows.forEach(r => {
      if (!by.has(r.material_id)) by.set(r.material_id, { ...r, links: [] });
      by.get(r.material_id).links.push(r);
    });
    return [...by.values()];
  }, [rows, view]);

  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));
  const apply = () => { setApplied(f); setPage(p => ({ ...p, skip: 0 })); };
  const reset = () => { setF(EMPTY); setApplied(EMPTY); setPage(p => ({ ...p, skip: 0 })); };

  const fetchAll = async () => api.listMappedMaterials(params({ skip: 0, limit: 5000 }));
  const doExport = async () => {
    setBusy('export');
    try { exportXlsx(await fetchAll() || [], COLUMNS, `Mapped-Materials-${project?.project_code || 'project'}`, 'Mapped Materials'); }
    catch (e) { toast?.show?.(e.message || 'Export failed', 'error'); }
    setBusy('');
  };
  const doPrint = async () => {
    setBusy('print');
    try { printRows(await fetchAll() || [], `Mapped Materials — ${project?.project_name || ''}`); }
    catch (e) { toast?.show?.(e.message || 'Print failed', 'error'); }
    setBusy('');
  };

  const statusOptions = [{ value: '', label: 'All' }, ...Object.entries(MAPPING_STATUS).filter(([k]) => k !== 'unmapped').map(([value, m]) => ({ value, label: m.label }))];
  const disc = Object.entries(stats?.by_discipline || {}).slice(0, 3);

  return (
    <div>
      <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Mapped Materials</h3>
      <p style={{ margin: '3px 0 14px', fontSize: 12.5, color: 'var(--text-secondary)' }}>
        Materials already linked to BOQ lines. Review, trace, verify and manage existing material-to-BOQ relationships.
      </p>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
          <StatTile label="Mapped Materials" value={stats.materials} accent={NAVY} />
          <StatTile label="Mapping Relationships" value={stats.relationships} accent="#334155" />
          {disc.map(([name, n]) => <StatTile key={name} label={name} value={n} accent="#7c3aed" />)}
          <StatTile label="Approved" value={stats.approved} accent="#16a34a" />
          <StatTile label="Pending Approval" value={stats.pending} accent="#ca8a04" />
        </div>
      )}

      <div style={{ border: '1px solid var(--border-light)', borderRadius: 12, padding: 12, marginBottom: 12, background: 'var(--bg-base)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          <Select label="Building" value={f.division} onChange={set('division')} options={asOptions(lists.divisions, 'project_division_id', 'All buildings')} />
          <Select label="Discipline" value={f.category} onChange={set('category')} options={asOptions(lists.categories, 'id', 'All disciplines')} />
          <Select label="Work Package" value={f.subsection} onChange={set('subsection')} options={asOptions(lists.subsections, 'id', 'All work packages')} />
          <Select label="Mapping Status" value={f.status} onChange={set('status')} options={statusOptions} />
          <Input label="BOQ Code" value={f.boq_code} onChange={set('boq_code')} placeholder="BOQ code" />
          <Input label="Material Code" value={f.material_code} onChange={set('material_code')} placeholder="material code" />
          <Input label="Mapped By" value={f.prepared_by} onChange={set('prepared_by')} placeholder="name" />
          <DatePicker label="Date From" value={f.date_from} onChange={set('date_from')} />
          <DatePicker label="Date To" value={f.date_to} onChange={set('date_to')} />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 300px' }}>
            <Input value={f.search} onChange={set('search')} placeholder="Search material code / description / BOQ / work package / mapping ref…"
                   onKeyDown={e => { if (e.key === 'Enter') apply(); }} />
          </div>
          <Btn onClick={apply}><Search size={14} /> Apply</Btn>
          <Btn variant="secondary" onClick={reset}><RotateCcw size={14} /> Reset</Btn>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'inline-flex', border: '1px solid black', borderRadius: 10, overflow: 'hidden' }}>
          {['flat', 'grouped'].map(v => (
            <button key={v} onClick={() => setView(v)} style={{ padding: '7px 16px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12.5,
              background: view === v ? NAVY : 'var(--bg-card)', color: view === v ? '#fff' : 'var(--text-primary)', fontFamily: 'var(--font)' }}>
              {v === 'flat' ? 'Flat' : 'Grouped by material'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="secondary" onClick={doExport} loading={busy === 'export'}><Download size={14} /> Export Excel</Btn>
          <Btn variant="secondary" onClick={doPrint} loading={busy === 'print'}><Printer size={14} /> Print</Btn>
        </div>
      </div>

      {rows === null ? <RNLLoader title="Loading mapped materials..." /> : view === 'flat' ? (
        <>
          <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['#', 'Material Code', 'Material', 'UOM', 'Building', 'Discipline', 'Work Package', 'BOQ Code', 'BOQ Description', 'BOQ Qty', 'Mapping Type', 'Mapping Ref', 'Status', 'Mapped By', 'Mapped Date', ''].map(h => (
                <th key={h} style={th}>{h}</th>))}</tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={16} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>No mapped materials match.</td></tr>}
                {rows.map((r, i) => (
                  <tr key={r.line_id}>
                    <td style={td}>{page.skip + i + 1}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.material_code}</td>
                    <td style={{ ...td, minWidth: 170 }}>{r.material_name}</td>
                    <td style={td}>{r.material_uom}</td>
                    <td style={td}>{r.building || '—'}</td>
                    <td style={td}>{r.discipline || '—'}</td>
                    <td style={td}>{r.work_package || '—'}</td>
                    <td style={{ ...td, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.boq_code}</td>
                    <td style={{ ...td, minWidth: 220, maxWidth: 360 }}>{r.description}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtQty(r.quantity)} {r.unit_of_measure}</td>
                    <td style={td}>{typeLabel(r.mapping_type)}</td>
                    <td style={{ ...td, fontFamily: 'monospace', color: NAVY, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.mapping_ref}</td>
                    <td style={td}><StatusPill status={r.status} /></td>
                    <td style={td}>{r.prepared_by || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(r.date_prepared)}</td>
                    <td style={td}><Btn style={{ padding: '4px 10px', fontSize: 11.5 }} variant="secondary" onClick={() => openMapper(r.boq_item_id)}>View</Btn></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12 }}><Pagination total={total} skip={page.skip} limit={page.limit} onChange={setPage} /></div>
        </>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {groups.length === 0 && <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)' }}>No mapped materials match.</div>}
          {groups.map(g => (
            <div key={g.material_id} style={{ border: '1px solid black', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ background: 'rgba(11,49,139,0.08)', padding: '8px 12px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 800, color: NAVY }}>{g.material_code}</span>
                <span style={{ fontWeight: 700 }}>{g.material_name}</span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{g.material_uom} · {g.material_category || '—'}</span>
                <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700 }}>{g.links.length} BOQ line{g.links.length === 1 ? '' : 's'}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {g.links.map(r => (
                    <tr key={r.line_id}>
                      <td style={{ ...td, width: 140 }}>{r.building || '—'}</td>
                      <td style={{ ...td, width: 160 }}>{r.work_package || '—'}</td>
                      <td style={{ ...td, fontFamily: 'monospace', width: 130 }}>{r.boq_code}</td>
                      <td style={td}>{r.description}</td>
                      <td style={{ ...td, width: 120 }}>{typeLabel(r.mapping_type)}</td>
                      <td style={{ ...td, width: 130 }}><StatusPill status={r.status} /></td>
                      <td style={{ ...td, width: 70 }}><Btn style={{ padding: '4px 10px', fontSize: 11.5 }} variant="secondary" onClick={() => openMapper(r.boq_item_id)}>View</Btn></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
